import { Link } from '@tanstack/react-router'

import { useIsAdmin } from '../lib/admin'
import { useSession } from '../lib/auth'
import { todayAtFacility } from '../lib/time'

/**
 * The phone navigation.
 *
 * The header nav is a flex row of up to seven links. At 390px it was 602px
 * wide, so every page scrolled sideways and the last items were cut off
 * mid-word — on every single page, for every signed-in customer. This replaces
 * it below `md`, where the horizontal row genuinely does not fit.
 *
 * ## Why a bottom bar rather than a hamburger or a scroller
 *
 * Most of this facility's customers arrive on a phone, and the three things
 * they do — book, check what they booked, see a tournament — are worth one tap
 * each rather than one tap plus a menu. A bar also puts them under the thumb,
 * which a top row cannot be.
 *
 * The cost is real and paid deliberately: it occupies a strip of every screen,
 * so `RootLayout` adds matching bottom padding, and it can only hold four
 * things. Everything else lives on the account page, which is what makes that
 * page a destination rather than a leftover.
 */

/** Emoji rather than an icon library. Adding a dependency to draw four glyphs
 *  is not a trade worth making, and these render everywhere without a build
 *  step. They are decorative — the label underneath is what is read, and the
 *  glyph is hidden from assistive tech. */
const TAB_CLASS =
  'flex min-h-16 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-xs ' +
  'text-slate-500 [&.active]:text-slate-900 [&.active]:font-semibold ' +
  'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-slate-900'

export function BottomTabs() {
  const session = useSession()
  const isAdmin = useIsAdmin()

  return (
    <nav
      aria-label="Navegación principal"
      className={[
        'fixed inset-x-0 bottom-0 z-20 flex border-t border-slate-200 bg-white md:hidden',
        // The home indicator on a modern iPhone sits over the bottom of the
        // viewport. Without this the last row of labels is under it.
        'pb-[env(safe-area-inset-bottom)]',
      ].join(' ')}
    >
      <Link
        to="/book"
        search={{ date: todayAtFacility() }}
        className={TAB_CLASS}
        activeProps={{ className: 'active' }}
      >
        <span aria-hidden="true" className="text-lg leading-none">
          ⛳
        </span>
        Reservar
      </Link>

      {session ? (
        <Link
          to="/bookings"
          search={{ tab: 'upcoming' }}
          className={TAB_CLASS}
          activeProps={{ className: 'active' }}
        >
          <span aria-hidden="true" className="text-lg leading-none">
            📅
          </span>
          Mis reservas
        </Link>
      ) : null}

      <Link to="/tournaments" className={TAB_CLASS} activeProps={{ className: 'active' }}>
        <span aria-hidden="true" className="text-lg leading-none">
          🏆
        </span>
        Torneos
      </Link>

      {session ? (
        <Link to="/me" className={TAB_CLASS} activeProps={{ className: 'active' }}>
          <span aria-hidden="true" className="text-lg leading-none">
            {/* An admin's fourth tab is still the account page -- the facility
                link lives on it. A fifth tab for one person is not worth the
                width it costs everybody else. */}
            {isAdmin ? '🔑' : '👤'}
          </span>
          Mi cuenta
        </Link>
      ) : (
        <Link
          to="/login"
          search={{ redirect: undefined }}
          className={TAB_CLASS}
          activeProps={{ className: 'active' }}
        >
          <span aria-hidden="true" className="text-lg leading-none">
            👤
          </span>
          Entrar
        </Link>
      )}
    </nav>
  )
}
